(() => {
  const TOKEN_PATTERN = /\[([a-z_]+)\]/g;

  const getLocalParts = (timeZone, date = new Date()) => new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23"
  }).formatToParts(date).reduce((result, part) => {
    result[part.type] = part.value;
    return result;
  }, {});

  const getLocalMinute = (timeZone, date = new Date()) => {
    const parts = getLocalParts(timeZone, date);
    return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`;
  };

  const normalizeLocalMinute = (value) => (value || "").trim().replace(" ", "T").slice(0, 16);

  const isEditorPreviewForced = () => Boolean(window.Shopify?.designMode)
    || new URLSearchParams(window.location.search).get("campaignWidgetQa") === "active";

  const isScheduledNow = (element) => {
    if (isEditorPreviewForced()) {
      return true;
    }

    try {
      const timeZone = element.getAttribute("time-zone") || "Europe/Bucharest";
      const now = getLocalMinute(timeZone);
      const start = normalizeLocalMinute(element.getAttribute("start-at"));
      const end = normalizeLocalMinute(element.getAttribute("end-at"));
      return (!start || now >= start) && (!end || now <= end);
    } catch (error) {
      console.warn("NUO product widget: invalid schedule", error);
      return false;
    }
  };

  const merge = (template, values) => (template || "").replace(
    TOKEN_PATTERN,
    (match, key) => values[key] ?? match
  );

  class NuoDeliveryEstimate extends HTMLElement {
    connectedCallback() {
      this.update();
      this.timer = window.setInterval(() => this.update(), 60000);
    }

    disconnectedCallback() {
      window.clearInterval(this.timer);
    }

    getNumber(name, fallback) {
      const value = Number.parseInt(this.getAttribute(name), 10);
      return Number.isFinite(value) ? value : fallback;
    }

    getHolidays() {
      return new Set((this.getAttribute("holidays") || "")
        .split(/[\s,;]+/)
        .map((value) => value.trim())
        .filter(Boolean));
    }

    isHoliday(date, holidays) {
      const year = String(date.getUTCFullYear());
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const day = String(date.getUTCDate()).padStart(2, "0");
      return holidays.has(`${day}-${month}`) || holidays.has(`${year}-${month}-${day}`);
    }

    update() {
      const target = this.querySelector("[data-nuo-delivery-date]");

      if (!target) {
        return;
      }

      try {
        const timeZone = this.getAttribute("time-zone") || "Europe/Bucharest";
        const locale = this.getAttribute("date-locale") || "ro-RO";
        const parts = getLocalParts(timeZone);
        const cutoffHour = this.getNumber("cutoff-hour", 13);
        const currentHour = Number.parseInt(parts.hour, 10);
        const date = new Date(Date.UTC(
          Number.parseInt(parts.year, 10),
          Number.parseInt(parts.month, 10) - 1,
          Number.parseInt(parts.day, 10)
        ));
        const weekday = date.getUTCDay();
        const isWeekend = weekday === 0 || weekday === 6;
        const businessDays = isWeekend || weekday === 5
          ? this.getNumber("days-weekend", 2)
          : currentHour < cutoffHour
            ? this.getNumber("days-before-cutoff", 1)
            : this.getNumber("days-after-cutoff", 2);
        const holidays = this.getHolidays();
        let addedDays = 0;

        while (addedDays < businessDays) {
          date.setUTCDate(date.getUTCDate() + 1);

          if (date.getUTCDay() !== 0 && date.getUTCDay() !== 6 && !this.isHoliday(date, holidays)) {
            addedDays += 1;
          }
        }

        target.textContent = new Intl.DateTimeFormat(locale, {
          weekday: "long",
          day: "numeric",
          month: this.getAttribute("date-style") === "long" ? "long" : "short",
          timeZone: "UTC"
        }).format(date);
      } catch (error) {
        console.warn("NUO delivery estimate: unable to calculate date", error);
      }
    }
  }

  class NuoProductCampaign extends HTMLElement {
    constructor() {
      super();
      this.cart = null;
      this.cartRequest = null;
      this.onCartChanged = this.onCartChanged.bind(this);
      this.onVariantChanged = this.onVariantChanged.bind(this);
      this.onQuantityChanged = this.onQuantityChanged.bind(this);
    }

    connectedCallback() {
      document.addEventListener("cart:change", this.onCartChanged);
      document.addEventListener("variant:change", this.onVariantChanged);
      document.addEventListener("click", this.onQuantityChanged);
      document.addEventListener("input", this.onQuantityChanged);
      document.addEventListener("change", this.onQuantityChanged);
      this.updateSchedule();
      this.scheduleTimer = window.setInterval(() => this.updateSchedule(), 30000);
    }

    disconnectedCallback() {
      document.removeEventListener("cart:change", this.onCartChanged);
      document.removeEventListener("variant:change", this.onVariantChanged);
      document.removeEventListener("click", this.onQuantityChanged);
      document.removeEventListener("input", this.onQuantityChanged);
      document.removeEventListener("change", this.onQuantityChanged);
      window.clearInterval(this.scheduleTimer);
    }

    getNumber(name) {
      const value = Number.parseFloat(this.getAttribute(name));
      return Number.isFinite(value) ? value : 0;
    }

    getBoolean(name) {
      return this.getAttribute(name) === "true";
    }

    updateSchedule() {
      const wasHidden = this.hidden;
      const active = this.getBoolean("enabled") && isScheduledNow(this);
      this.hidden = !active;

      if (!active) {
        return;
      }

      if (!this.cart && wasHidden) {
        this.refreshCart();
        return;
      }

      if (!this.cart) {
        this.refreshCart();
        return;
      }

      this.update();
    }

    getEligibleProductIds() {
      return new Set((this.getAttribute("eligible-product-ids") || "")
        .split(",")
        .map((value) => Number.parseInt(value, 10))
        .filter(Number.isFinite));
    }

    formatMoney(cents) {
      const locale = this.getAttribute("number-locale") || "ro-RO";
      const currencyLabel = this.getAttribute("currency-label") || "lei";
      const amount = new Intl.NumberFormat(locale, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(Math.max(0, cents) / 100);
      return `${amount} ${currencyLabel}`;
    }

    async refreshCart() {
      if (this.cartRequest) {
        return this.cartRequest;
      }

      this.cartRequest = (async () => {
        try {
          const root = window.Shopify?.routes?.root || "/";
          const response = await fetch(`${root}cart.js`, { headers: { Accept: "application/json" } });

          if (!response.ok) {
            throw new Error(`Cart request failed with ${response.status}`);
          }

          this.cart = await response.json();
          this.update();
        } catch (error) {
          console.warn("NUO product campaign: unable to read cart", error);
          this.hidden = true;
        } finally {
          this.cartRequest = null;
        }
      })();

      return this.cartRequest;
    }

    getEligibleTotal(cart) {
      const eligibleProductIds = this.getEligibleProductIds();
      const rewardProductId = this.getNumber("reward-product-id");
      const excludeReward = this.getBoolean("exclude-reward-product");
      const itemTotal = (cart.items || [])
        .filter((item) => item.requires_shipping)
        .filter((item) => !excludeReward || item.product_id !== rewardProductId)
        .filter((item) => eligibleProductIds.size === 0 || eligibleProductIds.has(item.product_id))
        .reduce((sum, item) => sum + item.final_line_price, 0);
      const cartDiscount = (cart.cart_level_discount_applications || [])
        .reduce((sum, discount) => sum + discount.total_allocated_amount, 0);
      return Math.max(0, itemTotal - cartDiscount);
    }

    getCurrentQuantity() {
      const formId = this.getAttribute("form-id");

      if (!formId) {
        return 1;
      }

      const input = document.querySelector(`input[name="quantity"][form="${CSS.escape(formId)}"]`);
      const quantity = Number.parseInt(input?.value, 10);
      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    update() {
      const enabled = this.getBoolean("enabled");
      const active = enabled && isScheduledNow(this);
      this.hidden = !active;

      if (!active || !this.cart) {
        return;
      }

      const threshold = this.getNumber("threshold");
      const currentProductId = this.getNumber("current-product-id");
      const currentVariantPrice = this.getNumber("current-variant-price");
      const currentQuantity = this.getCurrentQuantity();
      const rewardProductId = this.getNumber("reward-product-id");
      const eligibleProductIds = this.getEligibleProductIds();
      const candidateIsEligible = (eligibleProductIds.size === 0 || eligibleProductIds.has(currentProductId))
        && (!this.getBoolean("exclude-reward-product") || currentProductId !== rewardProductId);
      const rewardProductInCart = rewardProductId > 0
        && (this.cart.items || []).some((item) => item.product_id === rewardProductId);
      const cartTotal = this.getEligibleTotal(this.cart);
      const productSubtotal = currentVariantPrice * currentQuantity;
      const projectedTotal = cartTotal + (candidateIsEligible ? productSubtotal : 0);
      const qualified = cartTotal >= threshold;
      const productQualifies = !qualified && candidateIsEligible && projectedTotal >= threshold;
      let templateName = "message-pending";

      if (qualified) {
        templateName = "message-complete";
      } else if (rewardProductInCart && this.getAttribute("campaign-type") === "free_gift") {
        templateName = "message-reward-in-cart";
      } else if (productQualifies) {
        templateName = "message-product-qualifies";
      } else if (candidateIsEligible) {
        templateName = "message-after-add";
      }

      const values = {
        remaining_amount: this.formatMoney(threshold - cartTotal),
        remaining_after_add: this.formatMoney(threshold - projectedTotal),
        threshold: this.formatMoney(threshold),
        reward: this.getAttribute("reward-name") || "beneficiu",
        current_product: this.getAttribute("current-product-title") || "acest produs",
        cart_total: this.formatMoney(cartTotal),
        product_price: this.formatMoney(currentVariantPrice),
        product_subtotal: this.formatMoney(productSubtotal),
        quantity: String(currentQuantity)
      };
      const message = merge(this.getAttribute(templateName), values);
      const progress = threshold > 0 ? Math.min(cartTotal / threshold, 1) : 1;
      const projectedProgress = threshold > 0 ? Math.min(projectedTotal / threshold, 1) : 1;

      this.style.setProperty("--nuo-campaign-progress", progress);
      this.style.setProperty("--nuo-campaign-projected-progress", projectedProgress);
      this.querySelector("[data-nuo-campaign-message]")?.replaceChildren(message);

      const progressBar = this.querySelector("[role='progressbar']");
      progressBar?.setAttribute("aria-valuenow", String(Math.round(progress * 100)));
      this.dataset.state = qualified
        ? "qualified"
        : rewardProductInCart
          ? "reward-in-cart"
          : productQualifies
            ? "product-qualifies"
            : candidateIsEligible
              ? "after-add"
              : "pending";
    }

    onCartChanged(event) {
      if (event.detail?.cart) {
        this.cart = event.detail.cart;
        this.update();
      }
    }

    onVariantChanged(event) {
      if (event.detail?.formId !== this.getAttribute("form-id") || !event.detail.variant) {
        return;
      }

      this.setAttribute("current-variant-id", String(event.detail.variant.id || ""));
      this.setAttribute("current-variant-price", String(event.detail.variant.price || 0));
      this.update();
    }

    onQuantityChanged(event) {
      const target = event.target instanceof Element ? event.target : null;
      const quantityInput = target?.matches('input[name="quantity"]')
        ? target
        : target?.closest("quantity-selector")?.querySelector('input[name="quantity"]');

      if (!quantityInput || quantityInput.getAttribute("form") !== this.getAttribute("form-id")) {
        return;
      }

      this.update();
    }
  }

  if (!window.customElements.get("nuo-delivery-estimate")) {
    window.customElements.define("nuo-delivery-estimate", NuoDeliveryEstimate);
  }

  if (!window.customElements.get("nuo-product-campaign")) {
    window.customElements.define("nuo-product-campaign", NuoProductCampaign);
  }
})();
